import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import type { AccommodationListItem } from '@models/accommodations';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/confirmation.service';
import { CrmAccommodationsService } from '@services/crm-accommodations.service';
import { ToastService } from '@services/toast.service';
import { BoolComponent } from '@shared/components/bool/bool.component';
import { catchError, firstValueFrom, of } from 'rxjs';

@Component({
  selector: 'app-accommodation-list',
  templateUrl: './accommodation-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe,
    BoolComponent,
  ],
})
export class AccommodationListComponent {
  readonly waterId = input.required<number>();

  private readonly accommodationsService = inject(CrmAccommodationsService);
  private readonly router = inject(Router);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly deletingId = signal<number | null>(null);
  readonly displayedColumns = ['number', 'name', 'persons', 'spots', 'isActive', 'isFishingSpot', 'actions'];

  readonly accommodationsResource = rxResource({
    params: () => ({ waterId: this.waterId() }),
    stream: ({ params }) =>
      this.accommodationsService.getAccommodations(params.waterId).pipe(
        catchError(() => {
          this.toastService.showError(this.translate.instant('waters.accommodations.load-failed'));
          return of([] as AccommodationListItem[]);
        }),
      ),
  });

  readonly accommodations = computed(() => this.accommodationsResource.value() ?? []);
  readonly isLoading = this.accommodationsResource.isLoading;

  editAccommodation(id: number): void {
    this.router.navigate(['/waters/edit', this.waterId(), 'accommodations', 'edit', id]);
  }

  async deleteAccommodation(id: number): Promise<void> {
    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.accommodations.delete-title'),
      prompt: this.translate.instant('waters.accommodations.delete-confirm'),
    });
    if (!confirmed) return;

    this.deletingId.set(id);
    try {
      await firstValueFrom(this.accommodationsService.deleteAccommodation(this.waterId(), id));
      this.toastService.showSuccess(this.translate.instant('waters.accommodations.delete-success'));
      this.accommodationsResource.reload();
    } catch {
      this.toastService.showError(this.translate.instant('waters.accommodations.delete-failed'));
    } finally {
      this.deletingId.set(null);
    }
  }
}
